import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Edit3, Trash2, ImageOff } from 'lucide-react';
import { adminApi, absoluteMediaUrl } from './api';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';

const THEMES = ['sea', 'sand', 'sky', 'trees', 'unified'];
const CATEGORIES = ['marine', 'desert', 'forest', 'air', 'all'];
const STATUSES = ['draft', 'published'];

const AdminNewsList = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;
  const nav = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('');
  const [themeF, setThemeF] = useState('');
  const [categoryF, setCategoryF] = useState('');
  const [deleting, setDeleting] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusF) params.status = statusF;
    if (themeF) params.theme = themeF;
    if (categoryF) params.category = categoryF;
    try {
      const { data } = await adminApi.get('/news', { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  // Debounced filter refetch
  useEffect(() => {
    const id = setTimeout(fetchItems, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusF, themeF, categoryF]);

  const handleDelete = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    setDeleting(id);
    try {
      await adminApi.delete(`/news/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } finally {
      setDeleting(null);
    }
  };

  const themeChip = useMemo(() => ({
    sea: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-200',
    sand: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200',
    sky: 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200',
    trees: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200',
    unified: 'bg-gradient-to-r from-cyan-100 via-amber-100 to-emerald-100 text-slate-800 dark:from-cyan-500/20 dark:via-amber-500/20 dark:to-emerald-500/20 dark:text-white',
  }), []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex-1">{t.news}</h1>
        <button
          type="button"
          data-testid="admin-news-new"
          onClick={() => nav('/admin/news/new')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
                     bg-gradient-to-r from-brand-blue to-brand-blue-700
                     text-white text-sm font-semibold shadow-md hover:shadow-lg transition
                     ${isAr ? 'flex-row-reverse' : ''}`}
        >
          <Plus className="w-4 h-4" />
          {t.addArticle}
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div className={`relative md:col-span-1 ${isAr ? 'flex-row-reverse' : ''}`}>
            <Search className={`absolute top-3 ${isAr ? 'right-3' : 'left-3'} w-4 h-4 text-slate-400`} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.search}
              data-testid="admin-news-search"
              className={`w-full ${isAr ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3 text-left'} py-2.5 rounded-xl
                          bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                          text-sm text-slate-900 dark:text-white placeholder-slate-400
                          focus:outline-none focus:ring-2 focus:ring-brand-blue/40`}
            />
          </div>
          <Select value={statusF} setValue={setStatusF} options={STATUSES} placeholder={t.allStatuses} dir={isAr ? 'rtl' : 'ltr'} />
          <Select value={categoryF} setValue={setCategoryF} options={CATEGORIES} placeholder={t.allCategories} dir={isAr ? 'rtl' : 'ltr'} />
          <Select value={themeF} setValue={setThemeF} options={THEMES} placeholder={t.allThemes} dir={isAr ? 'rtl' : 'ltr'} />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 uppercase text-[11px] tracking-wider">
              <tr>
                <th className={`px-4 py-3 ${isAr ? 'text-right' : 'text-left'}`}></th>
                <th className={`px-4 py-3 ${isAr ? 'text-right' : 'text-left'}`}>{isAr ? 'العنوان' : 'Title'}</th>
                <th className={`px-4 py-3 ${isAr ? 'text-right' : 'text-left'}`}>{t.theme}</th>
                <th className={`px-4 py-3 ${isAr ? 'text-right' : 'text-left'}`}>{t.status}</th>
                <th className={`px-4 py-3 ${isAr ? 'text-right' : 'text-left'}`}>{t.publishDate}</th>
                <th className={`px-4 py-3 ${isAr ? 'text-right' : 'text-left'}`}>{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">{t.noData}</td></tr>
              ) : (
                items.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`admin-news-row-${a.id}`}
                    className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition"
                  >
                    <td className="px-4 py-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        {a.image ? (
                          <img src={absoluteMediaUrl(a.image)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageOff className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </td>
                    <td className={`px-4 py-3 ${isAr ? 'text-right' : 'text-left'}`}>
                      <Link to={`/admin/news/${a.id}`} className="font-medium text-slate-900 dark:text-white hover:text-brand-blue-700 dark:hover:text-cyan-300">
                        {isAr ? (a.title_ar || a.title_en || '—') : (a.title_en || a.title_ar || '—')}
                      </Link>
                      <div className="text-[11px] text-slate-500 font-mono">{a.id.slice(0, 8)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${themeChip[a.theme] || 'bg-slate-100 text-slate-700'}`}>
                        {a.theme}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        a.status === 'published'
                          ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                          : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'
                      }`}>
                        {a.status === 'published' ? t.published : t.draft}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[12px] font-mono text-slate-600 dark:text-slate-400">{a.publish_date || (a.updated_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className={`flex items-center gap-2 ${isAr ? 'flex-row-reverse' : ''}`}>
                        <Link
                          to={`/admin/news/${a.id}`}
                          data-testid={`admin-news-edit-${a.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-brand-blue/10 hover:text-brand-blue-700 dark:hover:text-cyan-300 text-xs font-semibold transition"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          {t.edit}
                        </Link>
                        <button
                          type="button"
                          disabled={deleting === a.id}
                          onClick={() => handleDelete(a.id)}
                          data-testid={`admin-news-delete-${a.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-700 dark:hover:text-red-300 text-xs font-semibold transition disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {t.delete}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Select = ({ value, setValue, options, placeholder, dir }) => (
  <select
    dir={dir}
    value={value}
    onChange={(e) => setValue(e.target.value)}
    className="w-full py-2.5 px-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
  >
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default AdminNewsList;
